import React, { useState } from 'react';
import { EyeSlashIcon } from '@heroicons/react/24/outline';
import Pagination from './Pagination';

/**
 * Operations hidden from the investigation timeline.
 * Data is still collected and stored in the case results blob for historical reference.
 * This list is display-only — it cannot be modified from the portal.
 */
const HIDDEN_OPERATIONS = [
  {
    operation: 'Send',
    logTypes: ['Exchange'],
    reason: 'Message trace provides richer detail for sent mail (recipient, subject, direction).',
  },
  {
    operation: 'SendAs',
    logTypes: ['Exchange'],
    reason: 'Message trace provides richer detail for sent mail (recipient, subject, direction).',
  },
  {
    operation: 'SendOnBehalf',
    logTypes: ['Exchange'],
    reason: 'Message trace provides richer detail for sent mail (recipient, subject, direction).',
  },
  {
    operation: 'ReceiveEmail',
    logTypes: ['Exchange'],
    reason: 'Server-side delivery event. MailItemsAccessed appears when the message is actually read.',
  },
  {
    operation: 'ReceiveMail',
    logTypes: ['Message Trace'],
    reason: 'Inbound mail delivery. MailItemsAccessed appears when the message is actually read.',
  },
  {
    operation: 'MessageReadReceiptReceived',
    logTypes: ['Exchange', 'General'],
    reason: 'System-generated read receipt notification. Not a user action.',
  },
  {
    operation: 'MipLabel',
    logTypes: ['Exchange', 'DLP'],
    reason: 'Automated Microsoft Information Protection sensitivity labeling. Not a user action.',
  },
  {
    operation: 'FileSyncUploadedFull',
    logTypes: ['SharePoint'],
    reason: 'Automated OneDrive sync client upload. FileUploaded appears for deliberate uploads.',
  },
  {
    operation: 'PagePrefetched',
    logTypes: ['SharePoint'],
    reason: 'Browser prefetch. PageViewed appears when the page is actually accessed.',
  },
];

export default function HiddenOperations() {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const totalPages = Math.max(1, Math.ceil(HIDDEN_OPERATIONS.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIdx = (safeCurrentPage - 1) * pageSize;
  const pagedOps = HIDDEN_OPERATIONS.slice(startIdx, startIdx + pageSize);

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <EyeSlashIcon className="h-7 w-7 text-slate-400" />
          <h1 className="text-2xl font-bold text-white">Hidden Operations</h1>
        </div>
        <p className="text-sm text-slate-400">
          These operations are filtered from the investigation timeline to reduce noise.
          The data is still collected and stored in the case results blob for historical reference.
        </p>
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        <table className="w-full text-sm text-left">
          <thead>
            <tr className="border-b border-slate-700 text-xs uppercase tracking-wider text-slate-400">
              <th className="px-4 py-3">Operation</th>
              <th className="px-4 py-3">Log Type(s)</th>
              <th className="px-4 py-3">Reason Hidden</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-700/50">
            {pagedOps.map((op) => (
              <tr key={op.operation} className="hover:bg-slate-800/40">
                <td className="px-4 py-3 font-mono text-slate-200">{op.operation}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1.5 flex-wrap">
                    {op.logTypes.map((lt) => (
                      <span
                        key={lt}
                        className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-slate-700 text-slate-300"
                      >
                        {lt}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3 text-slate-400">{op.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination
          currentPage={safeCurrentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
          totalItems={HIDDEN_OPERATIONS.length}
        />
      </div>
    </div>
  );
}
